/* eslint-disable @angular-eslint/no-output-on-prefix */
// -----------------------------------------------------------------------------
// <copyright file="form-input.component.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';

@Component({
  selector: 'vs-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormInputComponent implements OnChanges, OnInit, OnDestroy {
  control = input<FormControl>(new FormControl(null));
  inputType = input<string>('');
  placeHolder = input<string>('');
  errorMessage = input<string>('');
  maxLength = input<number>(0);
  tabindex = input<number>(0);
  touch = input<boolean>(false);
  af = input<any>(null);
  isReadonly = input<boolean>(false);
  isRequired = input<boolean>(true);
  showPasswordIcon = input<boolean>(false);
  showErrorMessage = input<boolean>(true);
  id = input<any>(null);
  validateOnBlur = input<boolean>(true);
  customErrorMsg = input<{ min: any; max: any }>({ min: null, max: null });
  autoFocus = input<boolean>(false);
  showLabel = input<boolean>(false);
  isRemoveAscii = input<boolean>(false);
  dataTestId = input<string | null>(null);
  onBlur = output<any>();
  onEnter = output<any>();
  onFocus = output<any>();
  keyUp = output<any>();
  onArrowPress = output<any>();
  onError = output<any>();
  keyDown = output<any>();
  keyPress = output<any>();

  // Signals for state management
  errorObj = signal({
    required: null,
    mailFormat: null,
    minlength: null,
    min: null,
    max: null,
    precision: null,
    maxlength: null,
    type: '',
    strong: null,
    specialChar: null,
    uppercase: null,
    lowercase: null,
    number: null,
    invalidValue: null as boolean | null,
  });
  requiredFlag = signal(false);
  autFo = signal(false);
  isActive = signal(false);
  error = signal(false);
  showPwdText = signal(false);

  public ngOnInit() {
    const ctrl = this.control();
    if (!ctrl) {
      return;
    }
    ctrl.valueChanges.subscribe(() => {
      if (this.isRemoveAscii()) {
        const originalValue = ctrl.value;
        const formattedValue = originalValue.replace(/[^\x20-\x7F\n\r\b]/g, '');
        if (formattedValue !== originalValue) {
          ctrl.setValue(formattedValue);
        }
      }
      this.ngOnChanges();
    });
  }
  public ngOnChanges() {
    const ctrl = this.control();
    if (ctrl && ctrl.errors) {
      this.requiredFlag.set(ctrl.errors?.['required']);
    }

    const errMsg = this.errorMessage();
    const showErr = this.showErrorMessage();
    if (errMsg && showErr) {
      this.error.set(true);
    }

    this.isError();
  }

  public showText(show: boolean) {
    this.showPwdText.set(show);
  }

  public isError() {
    const ctrl = this.control();
    const touch = this.touch();
    const showErr = this.showErrorMessage();

    if (touch && ctrl && !ctrl.touched) {
      if (ctrl.value) {
        this.isActive.set(true);
      }
      return;
    }

    if (ctrl && ctrl.dirty && ctrl.errors && showErr) {
      const errors = ctrl.errors;
      if (errors['strong']) {
        this.errorObj.update((obj) => ({ ...obj, strong: errors['strong'], type: 'strong' }));
      } else if (errors['minlength']) {
        this.errorObj.update((obj) => ({
          ...obj,
          minlength: errors['minlength'],
          type: 'minlength',
        }));
      } else if (errors['required']) {
        this.errorObj.update((obj) => ({
          ...obj,
          required: errors['required'],
          type: 'required',
        }));
      } else if (errors['email']) {
        if (ctrl.value) {
          this.errorObj.update((obj) => ({
            ...obj,
            mailFormat: errors['email'],
            type: 'mailFormat',
          }));
        } else {
          this.errorObj.update((obj) => ({ ...obj, type: '' }));
          this.error.set(false);
        }
      } else if (errors['precision']) {
        this.errorObj.update((obj) => ({
          ...obj,
          precision: errors['precision'],
          type: 'precision',
        }));
      } else if (errors['min']) {
        this.errorObj.update((obj) => ({ ...obj, min: errors['min'], type: 'min' }));
      } else if (errors['max']) {
        this.errorObj.update((obj) => ({ ...obj, max: errors['max'], type: 'max' }));
      } else if (errors['maxlength']) {
        this.errorObj.update((obj) => ({
          ...obj,
          maxlength: errors['maxlength'],
          type: 'maxlength',
        }));
      } else if (errors['specialChar']) {
        this.errorObj.update((obj) => ({
          ...obj,
          specialChar: errors['specialChar'],
          type: 'specialChar',
        }));
      } else if (errors['uppercase']) {
        this.errorObj.update((obj) => ({
          ...obj,
          uppercase: errors['uppercase'],
          type: 'uppercase',
        }));
      } else if (errors['lowercase']) {
        this.errorObj.update((obj) => ({
          ...obj,
          lowercase: errors['lowercase'],
          type: 'lowercase',
        }));
      } else if (errors['number']) {
        this.errorObj.update((obj) => ({ ...obj, number: errors['number'], type: 'number' }));
      } else if (errors['invalidValue']) {
        this.errorObj.update((obj) => ({ ...obj, invalidValue: true, type: 'invalidValue' }));
      } else if (ctrl.value && this.validateInputValue(ctrl.value)) {
        ctrl.setErrors({ incorrect: true });
        this.errorObj.update((obj) => ({ ...obj, invalidValue: true, type: 'invalidValue' }));
      }

      this.error.set(true);
      this.onError.emit(true);
    } else {
      this.errorObj.update((obj) => ({ ...obj, type: '' }));
      if (!this.errorMessage()) {
        this.error.set(false);
        this.onError.emit(false);
      }
    }

    const controlVal = this.control();
    if (
      !this.errorMessage() &&
      !this.error() &&
      controlVal &&
      controlVal.value !== '' &&
      controlVal.value !== null
    ) {
      this.isActive.set(true);
    } else {
      this.isActive.set(false);
    }
  }

  public offFocus(event: any) {
    const ctrl = this.control();
    const validateOnBlur = this.validateOnBlur();
    const inputType = this.inputType();

    if (validateOnBlur && ctrl) {
      if (ctrl.value && this.validateInputValue(ctrl.value) && inputType !== 'textarea') {
        ctrl.setErrors({ incorrect: true });
        this.errorObj.update((obj) => ({ ...obj, invalidValue: true, type: 'invalidValue' }));
        this.error.set(true);
        this.onError.emit(true);
        return;
      }
      const errors = ctrl.errors;
      if (errors) {
        for (const key in errors) {
          if (key === 'incorrect') {
            delete errors[key];
          }
        }
      }
      if (errors && !Object.keys(errors).length) {
        ctrl.setErrors(null);
      } else {
        ctrl.setErrors(errors);
      }
      if (inputType !== 'password' && inputType !== 'number' && ctrl.value) {
        ctrl.setValue(ctrl.value.trim ? ctrl.value.trim() : ctrl.value);
      }

      if (inputType === 'number' && !event.target.value && event.target.value !== 0) {
        event.target.value = null;
      }
      this.isError();
    }
    this.onBlur.emit(ctrl);
  }

  public validateInputValue(str: any) {
    for (let i = 0, n = str.length; i < n; i++) {
      //8364 is char code for €.. so for currency whitelist added it
      if ((str.charCodeAt(i) > 255 && str.charCodeAt(i) !== 8364) || str.charCodeAt(i) < 32) {
        return true;
      }
    }
    return false;
  }

  public keyup(event: any) {
    if (event.code === 'NumpadEnter' || event.code === 'Enter') {
      this.onEnter.emit(this.control);
    } else {
      const removedKeyCodes = [40, 38, 37, 39];
      if (removedKeyCodes.indexOf(event.keyCode) < 0) {
        this.keyUp.emit(this.control);
      } else {
        this.onArrowPress.emit(event);
      }
    }
    this.isError();
  }
  keydown(event: any) {
    this.keyDown.emit(event);
  }

  keypress(event: any) {
    this.keyPress.emit(event);
    // if(this.inputType === 'number' && this.maxLength) {
    //   if(this.control.value?.length >= this.maxLength) {
    //     return false;
    //   }
    // }
  }

  public focus() {
    this.onFocus.emit(this.control());
  }

  public ngOnDestroy() {
    // af is a readonly signal from input(), skip assignment
  }
}
